import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addFinalTreatmentOwnToProduct,
  getProductFinalTreatmentOwnList,
  updateProductFinalTreatmentOwn,
  deleteProductFinalTreatmentOwn,
} from "../../../../../../apis/Product/ProductFinalTreatmentOwnApi";
import { getDisassemblyComponentList } from "../../../../../../apis/Dropdown";
import {
  // getProdSurplusResourcesDropdown,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { useBonsaiData } from "../../../../../../hooks/useBonsaiData";

const FinalTreatmentOwnTable = ({ productId, category }) => {
  const { t } = useTranslation();
  // Use cached Bonsai data instead of individual API call
  const { data: bonsaiData, isLoading: isBonsaiLoading } = useBonsaiData();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [dropdownComponents, setDropdownComponents] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProductFinalTreatmentOwnList({
      productId,
      setObjects: setData,
      category: category,
    });
    // Removed - now using cached data
    getTransportCountryData({ setDropdownData: setDropdownCountry });
    getDisassemblyComponentList({
      setDropdownData: setDropdownComponents,
      id: productId,
    });
  }, [productId, category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFieldValue = useCallback((field) => {
    if (!field) return null;
    return typeof field === "object" ? field.value : field;
  }, []);

  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === editedItem.end_of_life_strategy.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      updateProductFinalTreatmentOwn({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        category: category,
        end_of_life_strategy: getFieldValue(editedItem.end_of_life_strategy),
        treatment_country: getFieldValue(editedItem.treatment_country),
        product_component: getFieldValue(editedItem.product_component),
        product_code: product_code,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData, category, getFieldValue, dropdownProcess]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === newItem.end_of_life_strategy.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      addFinalTreatmentOwnToProduct({
        productId,
        ...newItem,
        category: category,
        end_of_life_strategy: getFieldValue(newItem.end_of_life_strategy),
        treatment_country: getFieldValue(newItem.treatment_country),
        product_component: getFieldValue(newItem.product_component),
        product_code: product_code,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, category, getFieldValue, dropdownProcess]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductFinalTreatmentOwn({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "product_name",
        header: t("PRODUCT_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "is_disassembly",
        header: t("IS_DISASSEMBLY"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "product_component",
        header: t("PRODUCT_COMPONENT"),
        type: "dropdown",
        dropdownData: dropdownComponents,
        disableSortBy: true,
        width: "130px",
      },
      {
        accessor: "component_num_fu",
        header: `${t("No. of component per FU")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "end_of_life_strategy",
        header: `${t("END_OF_LIFE_STRATEGY")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "130px",
        // tipHeader: "Tip",
        // tipBody: t("end_of_life_strategy_NO_DATA_TIP"),
      },

      {
        accessor: "treatment_country",
        header: `${t("TREATMENT_COUNTRY")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        // tipHeader: "Tip",
        // tipBody: t("COUNTRY_PROCESSING_TIP"),
      },
      {
        accessor: "percentage_fu_treated",
        header: `${t("% of all FU treated this way")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: "%",
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, dropdownCountry, dropdownComponents, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      end_of_life_strategy: dropdownProcess,
      treatment_country: dropdownCountry,
      product_component: dropdownComponents,
    }),
    [dropdownProcess, dropdownCountry, dropdownComponents]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default FinalTreatmentOwnTable;
